/**
 * Order Service
 * Fetches orders from Supabase for order history and order confirmation.
 */

import { supabase, isSupabaseConfigured } from '@/config/supabase';

// ============================================
// Status Types (aligned with database constraints)
// ============================================

/**
 * Order status values (matches SQL CHECK constraint in migration 004)
 */
export type OrderStatus =
  | 'pending'
  | 'paid'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

/**
 * Production status values (matches SQL CHECK constraint in migration 005)
 */
export type ProductionStatus = 'placed' | 'confirmed' | 'queued' | 'completed' | 'shipped';

/**
 * Order item from Supabase
 */
export interface SupabaseOrderItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price_cents: number;
  front_design_url: string | null;
  back_design_url: string | null;
  thumbnail_url: string | null;
  design_mode: string | null;
  design_name: string | null;
}

/**
 * Order from Supabase
 */
export interface SupabaseOrder {
  id: string;
  stripe_session_id: string;
  customer_email: string;
  shipping_address: string;
  status: OrderStatus;
  total_amount_cents: number;
  order_number: string | null;
  order_notes: string | null;
  created_at: string;
  paid_at: string | null;
  // Production status tracking fields
  production_status: ProductionStatus | null;
  production_status_updated_at: string | null;
  queue_position: number | null;
  estimated_delivery_date: string | null;
  order_items: SupabaseOrderItem[];
}

/**
 * A single status history entry from the order_status_history table
 */
export interface OrderStatusHistoryEntry {
  id: string;
  order_id: string;
  status_type: 'order_status' | 'production_status';
  new_status: string;
  previous_status: string | null;
  changed_at: string;
  changed_by: 'system' | 'webhook' | 'admin' | 'sync';
  notes: string | null;
}

/**
 * Result from sync-status API
 */
export interface SyncStatusResult {
  synced: number;
  orders: Array<{
    orderId: string;
    orderNumber: string;
    productionStatus: ProductionStatus;
    queuePosition: number;
    estimatedDeliveryDate: string;
  }>;
}

/**
 * Fetch all orders for the authenticated user
 * Orders are matched by user_id OR customer_email
 */
export async function fetchOrders(): Promise<SupabaseOrder[]> {
  if (!isSupabaseConfigured()) {
    console.warn('[orderService] Supabase not configured');
    return [];
  }

  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    console.warn('[orderService] No authenticated session');
    return [];
  }

  try {
    const response = await fetch('/api/orders', {
      headers: {
        Authorization: `Bearer ${session.session.access_token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[orderService] API error:', error);
      throw new Error(error.error || 'Failed to fetch orders');
    }

    const data = await response.json();
    return data.orders || [];
  } catch (error) {
    console.error('[orderService] Failed to fetch orders:', error);
    throw error;
  }
}

/**
 * Fetch a single order by Stripe session ID
 * Used for order confirmation after checkout redirect
 */
export async function fetchOrderBySessionId(sessionId: string): Promise<SupabaseOrder | null> {
  if (!sessionId) {
    return null;
  }

  try {
    const response = await fetch(
      `/api/orders/by-session?session_id=${encodeURIComponent(sessionId)}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        // Order not found - might still be processing
        return null;
      }
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[orderService] API error:', error);
      throw new Error(error.error || 'Failed to fetch order');
    }

    const data = await response.json();
    return data.order || null;
  } catch (error) {
    console.error('[orderService] Failed to fetch order by session:', error);
    throw error;
  }
}

/**
 * Sync order statuses from Google Sheets
 * Updates production_status, queue_position, and estimated_delivery_date
 */
export async function syncOrderStatuses(): Promise<SyncStatusResult> {
  if (!isSupabaseConfigured()) {
    console.warn('[orderService] Supabase not configured');
    return { synced: 0, orders: [] };
  }

  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    console.warn('[orderService] No authenticated session');
    return { synced: 0, orders: [] };
  }

  try {
    const response = await fetch('/api/orders/sync-status', {
      headers: {
        Authorization: `Bearer ${session.session.access_token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[orderService] Sync status API error:', error);
      throw new Error(error.error || 'Unable to sync order status');
    }

    const data = await response.json();
    return data as SyncStatusResult;
  } catch (error) {
    console.error('[orderService] Failed to sync order statuses:', error);
    throw error;
  }
}

/**
 * Fetch status history timeline for a specific order
 */
export async function fetchOrderStatusHistory(
  orderId: string
): Promise<OrderStatusHistoryEntry[]> {
  if (!isSupabaseConfigured()) {
    console.warn('[orderService] Supabase not configured');
    return [];
  }

  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    console.warn('[orderService] No authenticated session');
    return [];
  }

  try {
    const response = await fetch(
      `/api/orders/status-history?order_id=${encodeURIComponent(orderId)}`,
      {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[orderService] Status history API error:', error);
      throw new Error(error.error || 'Failed to fetch status history');
    }

    const data = await response.json();
    return data.history || [];
  } catch (error) {
    console.error('[orderService] Failed to fetch status history:', error);
    throw error;
  }
}
